import { Chip, type ChipProps } from "@mui/material";
import { alpha } from "@mui/material/styles";

const formatStatusLabel = (status?: string) => {
  if (!status) {
    return "Unknown";
  }

  return status
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

interface StatusChipProps {
  status?: string;
  label?: string;
  size?: ChipProps["size"];
  sx?: ChipProps["sx"];
}

const StatusChip = ({
  status,
  label,
  size = "small",
  sx,
}: StatusChipProps) => {
  const normalizedStatus = status?.toUpperCase();

  return (
    <Chip
      label={label ?? formatStatusLabel(status)}
      size={size}
      sx={[
        (theme) => {
          const isFailure =
            normalizedStatus === "FAILED" ||
            normalizedStatus === "WRITE_FAILED" ||
            normalizedStatus === "FETCH_FAILED_SCRYFALL" ||
            normalizedStatus === "FETCH_FAILED_TAGGER";
          const isSuccess =
            normalizedStatus === "COMMITTED" || normalizedStatus === "WRITTEN";
          const isPending =
            normalizedStatus === "PENDING" ||
            normalizedStatus === "AWAITING_UPLOAD";
          const baseColor = isFailure
            ? theme.palette.secondary.main
            : isSuccess
              ? theme.palette.success.main
              : isPending
                ? theme.palette.grey[500]
                : theme.palette.primary.main;
          const backgroundColor = isSuccess
            ? baseColor
            : alpha(baseColor, theme.palette.mode === "dark" ? 0.24 : 0.14);
          const foregroundColor = isPending
            ? theme.palette.mode === "dark"
              ? theme.palette.grey[100]
              : theme.palette.grey[900]
            : theme.palette.getContrastText(baseColor);

          return {
            border: "1px solid",
            borderColor: isSuccess ? baseColor : alpha(baseColor, 0.42),
            backgroundColor,
            color: foregroundColor,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            fontSize: size === "small" ? "0.68rem" : "0.75rem",
            lineHeight: 1.1,
            "& .MuiChip-label": {
              px: size === "small" ? 0.9 : 1.15,
            },
          };
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
};

export default StatusChip;
